import {
  Bell,
  BookOpen,
  FileBadge,
  FileBarChart,
  FilePlus2,
  Gauge,
  Globe,
  GraduationCap,
  IdCard,
  Images,
  Inbox,
  ListChecks,
  LogIn,
  Mails,
  Medal,
  Newspaper,
  Palette,
  Plug,
  RefreshCw,
  Send,
  Server,
  ShieldCheck,
  Boxes,
  Tags,
  TriangleAlert,
  Type,
  User,
  UserPlus,
  Users,
  Video,
  Wand2,
  Zap,
} from 'lucide-react'
import { adminMenu, menuEntryById, permits, visibleMenu } from './navigation'
import { shell } from './strings'
import { strings as dashboard } from '../features/dashboard/strings'
import { categoriesStrings } from '../features/content/strings'
import { awarenessStrings, surveysStrings } from '../features/engagement/strings'
import { gamificationSettingsStrings } from '../features/gamification/strings'
import { licenseInformationStrings } from '../features/organisations/strings'
import { phishingSettingsStrings } from '../features/phishing/strings'
import { strings as programsStrings } from '../features/programs/strings'
import { settingsStrings } from '../features/settings/strings'

/**
 * Everywhere the command search can take you.
 *
 * The rail lists ~40 pages, and most of what an admin actually goes looking for
 * is not a page — it is a panel inside one. "إعدادات التنبيهات" is the eighth
 * section of /settings; "مستويات الخطر" is the third tab of the gamification
 * settings; "إعدادات سحب البيانات" is one of seven on /settings/advanced. None
 * of them are reachable by name from anywhere: you have to know which page owns
 * them and then find them on it. That is the gap this closes.
 *
 * Three kinds of destination, and the difference matters to the reader:
 *
 *   page     a rail entry, taken straight from navigation.js
 *   section  a tab or panel INSIDE a page — the route plus a hash
 *   action   something that opens its own screen (a form route)
 *
 * A part names the PAGE that owns it rather than repeating its route or its
 * trail. Everything else is derived: the route from the menu entry, the trail
 * from the group and item titles, and the role / licence / setting guard from
 * both — so a section of a page you may not open is never offered, and a rename
 * in the rail carries here with no edit.
 *
 * Labels are imported from the same strings each page renders, never re-typed.
 * A search result that says something the page does not is worse than no result:
 * you click it and land somewhere that looks wrong. This also means English is
 * free — the feature strings already have it.
 *
 * The hash IS the mechanism. `useHashTab` opens a page on the section its hash
 * names, and the shell scrolls to whatever declares `data-part` / `data-parts`.
 * No route, no backend, no state passed through navigation.
 */

/** A tab or panel inside a page. `id` is the hash the page answers to. */
const SECTIONS = [
  /* ---- Dashboard ---- */
  { page: 'general', id: 'kpi', title: dashboard.kpis.title, icon: Gauge },
  {
    page: 'general',
    id: 'participation',
    title: dashboard.kpis.groups.participation,
    icon: Users,
  },
  {
    page: 'general',
    id: 'learning',
    title: dashboard.kpis.groups.completion,
    icon: GraduationCap,
  },
  {
    page: 'general',
    id: 'compliance',
    title: dashboard.kpis.groups.compliance,
    icon: ShieldCheck,
  },

  /* ---- Settings: eight panels behind one SectionNav ---- */
  { page: 'settings', id: 'identity', title: settingsStrings.nav.identity, icon: Palette },
  { page: 'settings', id: 'media', title: settingsStrings.nav.media, icon: Images },
  { page: 'settings', id: 'keys', title: settingsStrings.nav.keys, icon: Type },
  { page: 'settings', id: 'contact', title: settingsStrings.nav.contact, icon: Mails },
  { page: 'settings', id: 'video', title: settingsStrings.nav.video, icon: Video },
  { page: 'settings', id: 'userManual', title: settingsStrings.nav.userManual, icon: BookOpen },
  { page: 'settings', id: 'reports', title: settingsStrings.nav.reports, icon: FileBarChart },
  {
    page: 'settings',
    id: 'notifications',
    title: settingsStrings.nav.notifications,
    icon: Bell,
  },

  /* ---- Advanced settings: seven, and all seven open with the same word ---- */
  { page: 'profileMapping', id: 'login', title: settingsStrings.nav.login, icon: LogIn },
  {
    page: 'profileMapping',
    id: 'registration',
    title: settingsStrings.nav.registration,
    icon: UserPlus,
  },
  { page: 'profileMapping', id: 'profile', title: settingsStrings.nav.profile, icon: IdCard },
  { page: 'profileMapping', id: 'sources', title: settingsStrings.nav.sources, icon: Plug },
  { page: 'profileMapping', id: 'sync', title: settingsStrings.nav.sync, icon: RefreshCw },
  { page: 'profileMapping', id: 'servers', title: settingsStrings.nav.servers, icon: Server },
  { page: 'profileMapping', id: 'services', title: settingsStrings.nav.services, icon: Boxes },

  /* ---- Gamification settings ---- */
  {
    page: 'pointsActivities',
    id: 'activities',
    title: gamificationSettingsStrings.tabs.activities,
    icon: Zap,
  },
  {
    page: 'pointsActivities',
    id: 'badges',
    title: gamificationSettingsStrings.tabs.badges,
    icon: Medal,
  },
  {
    page: 'pointsActivities',
    id: 'riskLevels',
    title: gamificationSettingsStrings.tabs.riskLevels,
    icon: TriangleAlert,
  },

  /* ---- Phishing settings ---- */
  {
    page: 'phishingCategories',
    id: 'categories',
    title: phishingSettingsStrings.tabs.categories,
    icon: Tags,
  },
  {
    page: 'phishingCategories',
    id: 'smtp',
    title: phishingSettingsStrings.tabs.smtp,
    icon: Send,
  },
  {
    page: 'phishingCategories',
    id: 'imap',
    title: phishingSettingsStrings.tabs.imap,
    icon: Inbox,
  },
  {
    page: 'phishingCategories',
    id: 'domains',
    title: phishingSettingsStrings.tabs.domains,
    icon: Globe,
  },
]

/** Screens you CREATE something on — each already has a route of its own. */
const ACTIONS = [
  {
    page: 'programs',
    id: 'new',
    title: programsStrings.actions.create,
    route: '/programs/new',
    icon: FilePlus2,
  },
  {
    page: 'programs',
    id: 'quick',
    title: programsStrings.actions.quickCreate,
    route: '/programs/quick',
    icon: Wand2,
  },
  {
    page: 'categories',
    id: 'new',
    title: categoriesStrings.actions.create,
    route: '/categories/new',
    icon: FilePlus2,
  },
  {
    page: 'awareness',
    id: 'new',
    title: awarenessStrings.create,
    route: '/awareness/new',
    icon: Newspaper,
  },
  {
    page: 'surveys',
    id: 'new',
    title: surveysStrings.create,
    route: '/surveys-management/new',
    icon: ListChecks,
  },
  /* `/phishing/templates/new` is deliberately absent. Its button is labelled
     `إضافة جديدة` — the only create action in the panel that does not name what
     it creates — and a result line reading "إضافة جديدة" under "صياد › القوالب"
     is a worse answer than the page itself, which is already indexed. */
]

/**
 * Reachable, but not from the rail — so nothing names them anywhere.
 *
 * `/license-information` has no menu entry in the legacy either: you get to it
 * by typing the URL. It takes the organisations group's guard, which is where
 * the other licence screen lives.
 */
const EXTRA_PAGES = [
  { id: 'profile', title: shell.profile, trail: [shell.account], to: '/profile', icon: User },
  {
    id: 'userPanel',
    title: shell.userPanel,
    trail: [shell.account],
    to: '/portal',
    icon: GraduationCap,
  },
  {
    id: 'licenseInformation',
    title: licenseInformationStrings.title,
    group: 'companiesGroup',
    to: '/license-information',
    icon: FileBadge,
  },
]

/**
 * Words that should find an entry without appearing on it.
 *
 * Keyed the way the index keys itself — `page` for a rail entry, `page:part`
 * for something inside one. A part's own id is NOT unique on its own: the
 * phishing settings have a `categories` tab and the rail has a `categories`
 * page, and /settings/advanced has a `profile` section next to the account's
 * own `profile` page.
 *
 * Two jobs, and the first one is not optional. Arabic forms its plurals by
 * reshaping the word rather than by adding to it — برنامج becomes البرامج,
 * موضوع becomes المواضيع, سؤال becomes الأسئلة — so a reader typing the singular
 * of the thing they are looking for gets NOTHING back from a substring match
 * against a menu written in the plural. Every list page carries its own singular
 * here for exactly that reason.
 *
 * The second job is the ordinary one: the word an admin reaches for when it is
 * not the word the panel uses. Deliberately thin — a keyword list that tries to
 * anticipate everything becomes a second vocabulary to maintain.
 */
const KEYWORDS = {
  general: ['dashboard', 'لوحة', 'رئيسية'],
  users: ['موظف', 'مستخدم', 'حساب', 'employee', 'account'],
  groups: ['قسم', 'إدارة', 'department'],
  programs: ['برنامج', 'دورة', 'program', 'course'],
  categories: ['موضوع', 'topic'],
  questions: ['سؤال', 'question'],
  certificateSettings: ['شهادة', 'certificate'],
  policiesSettings: ['سياسة', 'policy'],
  awareness: ['نشرة', 'مقال', 'article'],
  surveys: ['استبيان', 'survey'],
  assessments: ['قياس', 'assessment'],
  securityReports: ['بلاغ', 'حادثة', 'incident', 'report'],
  phishingTemplates: ['قالب', 'template'],
  phishingCampaigns: ['حملة', 'campaign'],
  manageCompanies: ['منظمة', 'شركة', 'company'],
  invitations: ['دعوة', 'invite'],

  settings: ['logo', 'شعار', 'brand'],
  'settings:identity': ['لون', 'شعار', 'colour', 'color', 'brand', 'theme'],
  'settings:media': ['شعار', 'logo', 'صورة', 'خلفية'],
  'phishingCategories:smtp': ['بريد', 'mail', 'email'],
  'phishingCategories:imap': ['بريد', 'mail', 'email'],
  'profileMapping:sync': ['ldap', 'active directory'],
  'profileMapping:sources': ['ldap', 'active directory'],
  'profileMapping:profile': ['حقول', 'fields'],
  pointsAnalysis: ['نقطة', 'نقاط', 'points', 'خطر', 'risk'],
  pointsTransactions: ['نقطة', 'نقاط', 'points'],
  systemState: ['خطأ', 'error', 'log'],
  licenseInformation: ['رخصة', 'ترخيص', 'license'],
  userPanel: ['متدرب', 'learner', 'portal'],
}

/* ── Folding ────────────────────────────────────────────────────────────────
   Arabic is typed several ways for the same word: with or without the hamza on
   an alif, with or without harakat, ة against ه, ى against ي. A reader hunting
   for الإعدادات types الاعدادات as often as not, and a plain `includes` finds
   nothing — which reads as "the panel has no such page".

   Folding maps every one of those to a single form. It also returns the index
   each folded character came from, so a hit can be highlighted on the ORIGINAL
   string: harakat are dropped, and without the map the marked range would drift
   left by one character per dropped mark. */

const FOLD = {
  أ: 'ا', إ: 'ا', آ: 'ا', ٱ: 'ا',
  ة: 'ه',
  ى: 'ي', ئ: 'ي',
  ؤ: 'و',
  'ً': '', 'ٌ': '', 'ٍ': '', 'َ': '', 'ُ': '',
  'ِ': '', 'ّ': '', 'ْ': '', 'ـ': '',
}

/** @returns {{text: string, map: number[]}} folded text, and where each char came from */
export function fold(value) {
  let text = ''
  const map = []
  for (let index = 0; index < value.length; index += 1) {
    const character = value[index]
    const folded = FOLD[character] ?? character.toLowerCase()
    if (!folded) continue
    text += folded
    map.push(index)
  }
  return { text, map }
}

const foldText = (value) => fold(value).text

/* ── The index ───────────────────────────────────────────────────────────── */

/** A page outranks a section of it, and a section outranks a form it opens. */
const KIND_WEIGHT = { page: 6, section: 3, action: 1 }

/**
 * `id` is the index's own key and has to be unique across the whole set — it is
 * what a recent destination is remembered by, and what React keys a result row
 * on. Pages use their rail id; anything inside a page is `page:part`, because a
 * part id alone is not unique (a `categories` tab under the phishing settings, a
 * `categories` page in the rail).
 */
const entry = ({ id, kind, title, trail, to, icon }) => {
  const folded = fold(title)
  return {
    id,
    kind,
    title,
    /** Group › page — what the result line prints under the title. */
    trail,
    to,
    icon,
    match: {
      title: folded.text,
      map: folded.map,
      trail: foldText(trail.join(' ')),
      keywords: foldText((KEYWORDS[id] ?? []).join(' ')),
    },
  }
}

/**
 * Every destination this session may open, pages first.
 *
 * Built once per session — it is ~90 entries of static text, and rebuilding it
 * per keystroke would fold every title again on every character typed.
 */
export function buildIndex(session) {
  const pages = visibleMenu(session).flatMap((group) =>
    group.items.map((item) =>
      entry({
        id: item.id,
        kind: 'page',
        title: item.title,
        trail: [group.title],
        to: item.to,
        icon: group.icon,
      }),
    ),
  )

  const extras = EXTRA_PAGES.filter((page) => {
    if (!page.group) return true
    const group = adminMenu.find((candidate) => candidate.id === page.group)
    return group ? permits(group, session) : false
  }).map((page) =>
    entry({
      id: page.id,
      kind: 'page',
      title: page.title,
      trail:
        page.trail ??
        [adminMenu.find((group) => group.id === page.group)?.title].filter(Boolean),
      to: page.to,
      icon: page.icon,
    }),
  )

  const inside = (list, kind) =>
    list
      .map((part) => ({ part, owner: menuEntryById(part.page) }))
      /* The page's own guard, both halves of it: a section is exactly as
         reachable as the page it is on. */
      .filter(
        ({ owner }) =>
          owner && permits(owner.group, session) && permits(owner.item, session),
      )
      .map(({ part, owner }) =>
        entry({
          id: `${part.page}:${part.id}`,
          kind,
          title: part.title,
          trail: [owner.group.title, owner.item.title],
          /* `part.id` unprefixed here — the prefix is the INDEX's key, the hash
             is what the page itself answers to. */
          to: part.route ?? `${owner.item.to}#${part.id}`,
          icon: part.icon,
        }),
      )

  return [...pages, ...extras, ...inside(SECTIONS, 'section'), ...inside(ACTIONS, 'action')]
}

/**
 * Rank one entry against the typed tokens.
 *
 * Every token has to appear SOMEWHERE in the entry, which is what lets
 * "اعدادات تنبيه" find the notifications panel — the two words are in the title
 * and the trail respectively. What the score then measures is how well the whole
 * needle sits in the TITLE, because that is the line the reader is scanning: a
 * title that starts with what you typed beats one that merely contains it, and
 * both beat an entry that only matched on its trail.
 */
function rank(candidate, needle, tokens) {
  const { title, trail, keywords } = candidate.match
  const hay = `${title} ${trail} ${keywords}`
  if (!tokens.every((token) => hay.includes(token))) return 0

  let score = KIND_WEIGHT[candidate.kind]
  if (title === needle) score += 100
  else if (title.startsWith(needle)) score += 70
  else if (title.includes(needle)) score += 50
  else if (tokens.every((token) => title.includes(token))) score += 34
  else if (keywords.includes(needle) || tokens.every((token) => keywords.includes(token)))
    score += 22
  else score += 10
  return score
}

/**
 * The best `limit` destinations for a query, already ordered.
 *
 * Ties keep index order, which is the rail's order — so when two panels score
 * the same, the one higher up the menu wins rather than whichever the sort
 * happened to touch last.
 */
export function searchIndex(index, query, limit = 9) {
  const needle = foldText(query.trim())
  if (!needle) return []
  const tokens = needle.split(/\s+/).filter(Boolean)

  return index
    .map((candidate, order) => ({ candidate, order, score: rank(candidate, needle, tokens) }))
    .filter((hit) => hit.score > 0)
    .sort((a, b) => b.score - a.score || a.order - b.order)
    .slice(0, limit)
    .map((hit) => ({ ...hit.candidate, marks: markRange(hit.candidate, needle, tokens) }))
}

/**
 * Where to draw the highlight on the ORIGINAL title — `[start, end)`, or null.
 *
 * The whole needle if the title carries it, otherwise the longest single token
 * that does. Marking every token separately turns a two-word query into a title
 * striped in three places, which is harder to read than no highlight at all.
 */
function markRange(candidate, needle, tokens) {
  const { title, map } = candidate.match
  const found = title.includes(needle)
    ? needle
    : tokens
        .filter((token) => title.includes(token))
        .sort((a, b) => b.length - a.length)[0]
  if (!found) return null
  const at = title.indexOf(found)
  return [map[at], map[at + found.length - 1] + 1]
}
